import { defaultWeights } from "../kernel/decisionKernel.js";

export const domains = [
  {
    id: "executive",
    label: "Executive Expansion",
    objective: "Choose the next expansion move with evidence-gated downside control.",
    weights: {
      ...defaultWeights,
      advantage: 1.2,
      information: 1.15,
      risk: 1.25,
      uncertainty: 1.15,
      disorder: 1.1
    },
    options: [
      {
        id: "enterprise-pilot",
        label: "Option A",
        title: "Enterprise pilot",
        variables: {
          advantage: 0.78,
          information: 0.72,
          timing: 0.68,
          position: 0.74,
          energy: 0.62,
          harmony: 0.7,
          cost: 0.44,
          risk: 0.48,
          uncertainty: 0.38,
          exposure: 0.32,
          disorder: 0.28
        },
        gates: [
          { label: "customer evidence", required: true, passed: true },
          { label: "legal review", required: true, passed: true },
          { label: "delivery capacity", required: true, passed: true }
        ],
        evidence: ["signed discovery notes", "implementation estimate", "named buyer"],
        missingEvidence: ["security questionnaire"],
        nextAction: "Request security scope and convert pilot into a dated decision packet."
      },
      {
        id: "mass-market",
        label: "Option B",
        title: "Mass-market launch",
        variables: {
          advantage: 0.64,
          information: 0.42,
          timing: 0.58,
          position: 0.54,
          energy: 0.5,
          harmony: 0.46,
          cost: 0.68,
          risk: 0.72,
          uncertainty: 0.76,
          exposure: 0.64,
          disorder: 0.58
        },
        gates: [
          { label: "demand proof", required: true, passed: false },
          { label: "unit economics", required: true, passed: false },
          { label: "support capacity", required: true, passed: true }
        ],
        evidence: ["prototype traffic"],
        missingEvidence: ["paid conversion data", "support load test"],
        nextAction: "Block launch claim until acquisition and support proof exist."
      },
      {
        id: "partner-channel",
        label: "Option C",
        title: "Partner channel",
        variables: {
          advantage: 0.7,
          information: 0.6,
          timing: 0.74,
          position: 0.69,
          energy: 0.58,
          harmony: 0.75,
          cost: 0.36,
          risk: 0.42,
          uncertainty: 0.52,
          exposure: 0.4,
          disorder: 0.34
        },
        gates: [
          { label: "partner authority", required: true, passed: true },
          { label: "margin model", required: true, passed: true },
          { label: "contract path", required: true, passed: true }
        ],
        evidence: ["partner intro", "margin worksheet"],
        missingEvidence: ["joint go-to-market calendar"],
        nextAction: "Draft channel offer and verify partner timeline."
      }
    ]
  },
  {
    id: "personal",
    label: "Personal Decision",
    objective: "Choose a personal move with reversibility, energy, and values alignment visible.",
    weights: {
      ...defaultWeights,
      harmony: 1.35,
      energy: 1.2,
      risk: 1.15,
      cost: 1.1
    },
    options: [
      {
        id: "deep-work-sprint",
        label: "Option A",
        title: "Deep-work sprint",
        variables: {
          advantage: 0.62,
          information: 0.76,
          timing: 0.72,
          position: 0.55,
          energy: 0.82,
          harmony: 0.78,
          cost: 0.28,
          risk: 0.18,
          uncertainty: 0.32,
          exposure: 0.18,
          disorder: 0.24
        },
        gates: [
          { label: "calendar protected", required: true, passed: true },
          { label: "health floor", required: true, passed: true }
        ],
        evidence: ["available mornings", "clear output target"],
        missingEvidence: [],
        nextAction: "Lock a two-week sprint and measure output quality daily."
      },
      {
        id: "new-commitment",
        label: "Option B",
        title: "New commitment",
        variables: {
          advantage: 0.56,
          information: 0.48,
          timing: 0.5,
          position: 0.48,
          energy: 0.38,
          harmony: 0.42,
          cost: 0.64,
          risk: 0.52,
          uncertainty: 0.58,
          exposure: 0.46,
          disorder: 0.66
        },
        gates: [
          { label: "capacity confirmed", required: true, passed: false },
          { label: "exit criteria", required: true, passed: false }
        ],
        evidence: ["initial upside"],
        missingEvidence: ["capacity check", "exit rule"],
        nextAction: "Do not accept until capacity and exit criteria are written."
      },
      {
        id: "relationship-repair",
        label: "Option C",
        title: "Relationship repair",
        variables: {
          advantage: 0.5,
          information: 0.58,
          timing: 0.6,
          position: 0.44,
          energy: 0.52,
          harmony: 0.86,
          cost: 0.32,
          risk: 0.3,
          uncertainty: 0.44,
          exposure: 0.36,
          disorder: 0.28
        },
        gates: [
          { label: "safe context", required: true, passed: true },
          { label: "specific ask", required: true, passed: true }
        ],
        evidence: ["known issue", "clear repair action"],
        missingEvidence: ["best timing"],
        nextAction: "Send one precise repair message and avoid expanding scope."
      }
    ]
  },
  {
    id: "trading",
    label: "Trading Research",
    objective: "Rank research theses without presenting research output as financial advice.",
    weights: {
      ...defaultWeights,
      information: 1.35,
      risk: 1.45,
      exposure: 1.35,
      uncertainty: 1.25
    },
    options: [
      {
        id: "trend-continuation",
        label: "Option A",
        title: "Trend continuation thesis",
        variables: {
          advantage: 0.64,
          information: 0.62,
          timing: 0.66,
          position: 0.56,
          energy: 0.58,
          harmony: 0.52,
          cost: 0.24,
          risk: 0.62,
          uncertainty: 0.54,
          exposure: 0.7,
          disorder: 0.48
        },
        gates: [
          { label: "risk limit", required: true, passed: true },
          { label: "invalidation", required: true, passed: true },
          { label: "live data verified", required: true, passed: false }
        ],
        evidence: ["historic regime notes", "invalidation sketch"],
        missingEvidence: ["current market data", "liquidity check"],
        nextAction: "Keep as research only until live data and liquidity are verified."
      },
      {
        id: "mean-reversion",
        label: "Option B",
        title: "Mean reversion thesis",
        variables: {
          advantage: 0.5,
          information: 0.54,
          timing: 0.44,
          position: 0.48,
          energy: 0.5,
          harmony: 0.46,
          cost: 0.2,
          risk: 0.58,
          uncertainty: 0.66,
          exposure: 0.6,
          disorder: 0.62
        },
        gates: [
          { label: "risk limit", required: true, passed: true },
          { label: "invalidation", required: true, passed: false },
          { label: "live data verified", required: true, passed: false }
        ],
        evidence: ["rough setup"],
        missingEvidence: ["invalidation", "current market data"],
        nextAction: "Blocked until invalidation and source data are attached."
      },
      {
        id: "cash-wait",
        label: "Option C",
        title: "Wait for clearer regime",
        variables: {
          advantage: 0.44,
          information: 0.72,
          timing: 0.62,
          position: 0.7,
          energy: 0.66,
          harmony: 0.72,
          cost: 0.12,
          risk: 0.14,
          uncertainty: 0.3,
          exposure: 0.1,
          disorder: 0.18
        },
        gates: [
          { label: "capital preservation", required: true, passed: true },
          { label: "review trigger", required: true, passed: true }
        ],
        evidence: ["defined trigger", "low exposure"],
        missingEvidence: [],
        nextAction: "Set review trigger and preserve optionality."
      }
    ]
  },
  {
    id: "negotiation",
    label: "Negotiation / Influence",
    objective: "Choose the next influence move with lawful communication, reversibility, and low coercion risk visible.",
    weights: {
      ...defaultWeights,
      information: 1.35,
      risk: 1.4,
      uncertainty: 1.2,
      disorder: 1.1
    },
    options: [
      {
        id: "set-frame",
        label: "Option A",
        title: "Set a principled frame",
        variables: {
          advantage: 0.66,
          information: 0.74,
          timing: 0.58,
          position: 0.7,
          energy: 0.54,
          harmony: 0.82,
          cost: 0.28,
          risk: 0.3,
          uncertainty: 0.34,
          exposure: 0.56,
          disorder: 0.28
        },
        gates: [
          { label: "lawful communication", required: true, passed: true },
          { label: "consent path", required: true, passed: true },
          { label: "scope defined", required: true, passed: true }
        ],
        evidence: ["framing draft", "stakeholder map"],
        missingEvidence: ["counter-frame fallback"],
        nextAction: "Lock the frame with explicit scope and keep communication auditable."
      },
      {
        id: "quiet-switch",
        label: "Option B",
        title: "Quietly switch leverage",
        variables: {
          advantage: 0.62,
          information: 0.58,
          timing: 0.7,
          position: 0.62,
          energy: 0.44,
          harmony: 0.48,
          cost: 0.3,
          risk: 0.52,
          uncertainty: 0.48,
          exposure: 0.36,
          disorder: 0.4
        },
        gates: [
          { label: "lawful communication", required: true, passed: true },
          { label: "coercion review", required: true, passed: false }
        ],
        evidence: ["leverage options"],
        missingEvidence: ["coercion risk review"],
        nextAction: "Block until coercion and consent checks are complete."
      },
      {
        id: "exit-loop",
        label: "Option C",
        title: "Exit and reset",
        variables: {
          advantage: 0.44,
          information: 0.66,
          timing: 0.54,
          position: 0.58,
          energy: 0.52,
          harmony: 0.76,
          cost: 0.14,
          risk: 0.16,
          uncertainty: 0.3,
          exposure: 0.18,
          disorder: 0.18
        },
        gates: [
          { label: "lawful communication", required: true, passed: true },
          { label: "exit condition written", required: true, passed: true }
        ],
        evidence: ["exit rules", "fallback plan"],
        missingEvidence: [],
        nextAction: "Use reset leverage only when reversibility and reputational boundaries are explicit."
      }
    ]
  },
  {
    id: "operating",
    label: "Operating System",
    objective: "Choose the next operating-system action by reducing disorder and improving proof density.",
    weights: {
      ...defaultWeights,
      information: 1.25,
      harmony: 1.15,
      disorder: 1.45,
      uncertainty: 1.25
    },
    options: [
      {
        id: "source-ledger",
        label: "Option A",
        title: "Source-of-truth ledger",
        variables: {
          advantage: 0.66,
          information: 0.88,
          timing: 0.74,
          position: 0.72,
          energy: 0.6,
          harmony: 0.8,
          cost: 0.3,
          risk: 0.16,
          uncertainty: 0.18,
          exposure: 0.12,
          disorder: 0.12
        },
        gates: [
          { label: "owner known", required: true, passed: true },
          { label: "write path safe", required: true, passed: true }
        ],
        evidence: ["repo path", "registry schema"],
        missingEvidence: [],
        nextAction: "Write the source ledger and make verification receipts mandatory."
      },
      {
        id: "automation-stack",
        label: "Option B",
        title: "Automation stack",
        variables: {
          advantage: 0.72,
          information: 0.5,
          timing: 0.46,
          position: 0.56,
          energy: 0.48,
          harmony: 0.5,
          cost: 0.58,
          risk: 0.48,
          uncertainty: 0.62,
          exposure: 0.38,
          disorder: 0.58
        },
        gates: [
          { label: "source ledger", required: true, passed: false },
          { label: "rollback path", required: true, passed: false }
        ],
        evidence: ["automation idea"],
        missingEvidence: ["source ledger", "rollback proof"],
        nextAction: "Block automation until source-of-truth and rollback are verified."
      },
      {
        id: "weekly-review",
        label: "Option C",
        title: "Weekly review cadence",
        variables: {
          advantage: 0.52,
          information: 0.7,
          timing: 0.78,
          position: 0.52,
          energy: 0.64,
          harmony: 0.76,
          cost: 0.2,
          risk: 0.12,
          uncertainty: 0.24,
          exposure: 0.12,
          disorder: 0.2
        },
        gates: [
          { label: "calendar slot", required: true, passed: true },
          { label: "receipt format", required: true, passed: true }
        ],
        evidence: ["cadence", "receipt fields"],
        missingEvidence: [],
        nextAction: "Run the cadence after the ledger is in place."
      }
    ]
  }
];

export function getDomain(domainId) {
  return domains.find((domain) => domain.id === domainId) ?? domains[0];
}
